import { useMemo, useState } from "react";
import {
  useRadarFileCommit,
  useRadarFilePreview,
  useDispatchRadarEmails,
  useRadarPreview,
  useQueueRadarReadyAlerts,
  useRecheckRadarIdentityRequest,
  useRemoveRadarDistributionMember,
  useResolveRadarIdentityRequest,
  useRadarRun,
  useRadarAdminStatus,
  useRadarV2AdminStatus,
  useRadarV2DraftBundles,
  useRadarV2IntakeQueue,
  useRadarV2Preview,
  useRadarV2ShadowBuild,
  useConfirmRadarV2Intake,
  useSaveRadarHuddle,
  useSetRadarArchiveAccess,
  useStageRadarV2Intake,
  useUpdateRadarSettings,
  useUpdateRadarSource,
  useUpsertRadarDistributionMember,
} from "./hooks";

function label(value: string): string {
  return value.replaceAll("_", " ").replace(/\b\w/g, (character) => character.toUpperCase());
}
export function RadarAdmin(): JSX.Element {
  const status = useRadarAdminStatus();
  const v2Status = useRadarV2AdminStatus(status.data?.admin?.canConfigure === true);
  const v2Intake = useRadarV2IntakeQueue(status.data?.admin?.canConfigure === true);
  const v2Preview = useRadarV2Preview();
  const v2ShadowBuild = useRadarV2ShadowBuild();
  const v2DraftBundles = useRadarV2DraftBundles();
  const confirmV2Intake = useConfirmRadarV2Intake();
  const stageV2Intake = useStageRadarV2Intake();
  const preview = useRadarPreview();
  const run = useRadarRun();
  const queueAlerts = useQueueRadarReadyAlerts();
  const dispatchEmails = useDispatchRadarEmails();
  const updateSettings = useUpdateRadarSettings();
  const updateSource = useUpdateRadarSource();
  const intakePreview = useRadarFilePreview();
  const intakeCommit = useRadarFileCommit();
  const saveHuddle = useSaveRadarHuddle();
  const setArchiveAccess = useSetRadarArchiveAccess();
  const recheckIdentity = useRecheckRadarIdentityRequest();
  const resolveIdentity = useResolveRadarIdentityRequest();
  const upsertDistribution = useUpsertRadarDistributionMember();
  const removeDistribution = useRemoveRadarDistributionMember();
  const [practice, setPractice] = useState("te");
  const [intakeSource, setIntakeSource] = useState("opened_projects");
  const [intakeDate, setIntakeDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [intakeFile, setIntakeFile] = useState<File | null>(null);
  const [distributionPractice, setDistributionPractice] = useState("te");
  const [distributionName, setDistributionName] = useState("");
  const [distributionEmail, setDistributionEmail] = useState("");
  const [distributionTitle, setDistributionTitle] = useState("");
  const [archiveAccessEmail, setArchiveAccessEmail] = useState("");
  const [identitySelections, setIdentitySelections] = useState<Record<string, string>>({});
  const note =
    status.data?.admin?.huddleNotes.find((item) => item.practice_key === practice)
      ?.facilitator_notes ?? "";
  const [draftNotes, setDraftNotes] = useState<Record<string, string>>({});
  const notes = draftNotes[practice] ?? note;
  const score = useMemo(() => {
    const rows = status.data?.admin?.scorecardRows ?? [];
    const byPractice = new Map<
      string,
      { active: number; meetings: number; proposals: number; won: number }
    >();
    for (const row of rows) {
      const key = row.radar_opportunities?.practice_key ?? "unassigned";
      const current = byPractice.get(key) ?? { active: 0, meetings: 0, proposals: 0, won: 0 };
      if (row.status === "active") current.active += 1;
      if (row.stage === "meeting") current.meetings += 1;
      if (row.stage === "proposal") current.proposals += 1;
      if (row.stage === "won") current.won += 1;
      byPractice.set(key, current);
    }
    return [...byPractice.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [status.data?.admin?.scorecardRows]);
  const intakeSources = (status.data?.sources ?? []).filter(
    (source) => source.intake_mode === "email" || source.intake_mode === "upload"
  );
  const activeDistribution = (status.data?.admin?.distribution ?? []).filter(
    (member) => member.is_active
  );
  const removedDistributionCount = (status.data?.admin?.distribution ?? []).filter(
    (member) => !member.is_active
  ).length;
  const intakePayload = intakeFile
    ? { sourceKey: intakeSource, file: intakeFile, observedAt: `${intakeDate}T12:00:00.000Z` }
    : null;

  return (
    <details className="settings-disclosure" open>
      <summary className="settings-disclosure__summary">
        <span>
          <strong>RADAR administration</strong>
          <small>Govern source intake, distribution, staging proofs, huddles and scorecards.</small>
        </span>
        <span className="settings-disclosure__chevron" aria-hidden="true">
          ⌄
        </span>
      </summary>
      <div className="settings-disclosure__body radar-admin">
        {status.isPending ? <p>Loading RADAR controls…</p> : null}
        {status.isError ? (
          <p className="radar-error">RADAR administration is not available yet.</p>
        ) : null}
        {status.data ? (
          <>
            <div className="radar-notice">
              <strong>{status.data.enabled ? "RADAR active" : "RADAR dark-launched"}</strong> ·
              Email {status.data.emailEnabled ? "enabled" : "disabled"} · Inbound email{" "}
              {status.data.inboundEmailEnabled ? "enabled" : "disabled"} · Links expire after{" "}
              {status.data.magicLinkDays} days.
            </div>
            {status.data.admin?.canConfigure ? (
              <section className="radar-v2-admin" aria-labelledby="radar-v2-heading">
                <h3 id="radar-v2-heading">RADAR v2 controlled build</h3>
                <p className="radar-muted">
                  V2 runs alongside the current engine. Preview and shadow actions cannot send
                  production email; the hard production-send gate remains locked until all release
                  checks pass and the owner approves the completion report.
                </p>
                {v2Status.data ? (
                  <div className="radar-preview">
                    Engine <strong>{v2Status.data.settings.engineVersion}</strong> · Shadow{" "}
                    <strong>{v2Status.data.settings.shadowEnabled ? "enabled" : "off"}</strong> ·
                    Production send{" "}
                    <strong>
                      {v2Status.data.settings.productionSendEnabled ? "enabled" : "locked"}
                    </strong>{" "}
                    · {v2Status.data.intakeQueueCount} intake item(s) awaiting review ·{" "}
                    {v2Status.data.reviewQueueCount} governed review item(s).
                  </div>
                ) : null}
                <div className="radar-admin-actions">
                  <button
                    type="button"
                    disabled={v2Preview.isPending}
                    onClick={() => v2Preview.mutate()}
                  >
                    {v2Preview.isPending ? "Evaluating v2…" : "Preview v2 qualification"}
                  </button>
                  <button
                    type="button"
                    disabled={
                      v2ShadowBuild.isPending || v2Status.data?.settings.shadowEnabled !== true
                    }
                    onClick={() => v2ShadowBuild.mutate()}
                  >
                    {v2ShadowBuild.isPending ? "Building shadow…" : "Build v2 shadow"}
                  </button>
                  <button
                    type="button"
                    disabled={
                      v2DraftBundles.isPending || v2Status.data?.settings.shadowEnabled !== true
                    }
                    onClick={() => v2DraftBundles.mutate()}
                  >
                    {v2DraftBundles.isPending
                      ? "Rendering draft bundles…"
                      : "Render v2 draft emails"}
                  </button>
                </div>
                {v2Preview.data ? (
                  <div className="radar-preview">
                    Previewed <strong>{v2Preview.data.summary.accountsEvaluated}</strong> accounts ·{" "}
                    <strong>{v2Preview.data.summary.qualifyingRoutes}</strong> qualifying routes ·
                    no writes and no email.
                  </div>
                ) : null}
                {v2ShadowBuild.data ? (
                  <div className="radar-preview">
                    Shadow run <strong>{v2ShadowBuild.data.runId}</strong> completed ·{" "}
                    {v2ShadowBuild.data.summary.routes} routes persisted for verification only.
                  </div>
                ) : null}
                {v2DraftBundles.data ? (
                  <div className="radar-preview">
                    <strong>{v2DraftBundles.data.recipientBundles.length}</strong> recipient bundles
                    rendered as drafts · production send remains{" "}
                    <strong>{v2DraftBundles.data.sendEnabled ? "enabled" : "locked"}</strong>.
                  </div>
                ) : null}
                {v2Status.isError ||
                v2Preview.isError ||
                v2ShadowBuild.isError ||
                v2DraftBundles.isError ? (
                  <p className="radar-error">
                    The v2 staging action did not complete. V1 and production delivery were not
                    changed.
                  </p>
                ) : null}

                <h4>Controlled v2 intake queue</h4>
                <p className="radar-muted">
                  Incoming attachments remain private and inert until an administrator confirms the
                  detected report type, practice, sheet and column mapping.
                </p>
                <div className="radar-file-intake">
                  <label>
                    Governed source
                    <select
                      value={intakeSource}
                      onChange={(event) => setIntakeSource(event.target.value)}
                    >
                      {intakeSources.map((source) => (
                        <option key={source.source_key} value={source.source_key}>
                          {source.display_name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Report date
                    <input
                      type="date"
                      value={intakeDate}
                      onChange={(event) => setIntakeDate(event.target.value)}
                    />
                  </label>
                  <label>
                    Report file
                    <input
                      type="file"
                      accept=".xlsx,.pdf,.csv,.txt"
                      onChange={(event) => setIntakeFile(event.target.files?.[0] ?? null)}
                    />
                  </label>
                  <button
                    type="button"
                    disabled={!intakePayload || stageV2Intake.isPending}
                    onClick={() => intakePayload && stageV2Intake.mutate(intakePayload)}
                  >
                    {stageV2Intake.isPending ? "Staging report…" : "Stage for governed review"}
                  </button>
                </div>
                {stageV2Intake.data ? (
                  <p className="radar-preview">
                    The report is staged and inert. Review its detected mapping below before
                    committing any evidence.
                  </p>
                ) : null}
                {v2Intake.data?.length ? (
                  <div className="radar-distribution-list">
                    {v2Intake.data.map((item) => (
                      <article key={item.load_id}>
                        <div>
                          <strong>{item.original_filename || "Unnamed attachment"}</strong>
                          <small>
                            {item.detected_report_type || "Unknown report"} ·{" "}
                            {item.detected_practice?.toUpperCase() || "Practice unresolved"} ·{" "}
                            {item.record_count} candidate rows
                          </small>
                          <small>
                            Sheet: {item.detected_tab || "Not detected"} · Mapping:{" "}
                            {Object.entries(item.column_mapping ?? {})
                              .map(([field, column]) => `${label(field)} → ${String(column)}`)
                              .join(" · ") || "Not detected"}
                          </small>
                          <small>
                            {item.sender_email || "Unknown sender"} · {label(item.status)}
                          </small>
                        </div>
                        {item.status === "awaiting_confirmation" ? (
                          <button
                            type="button"
                            disabled={confirmV2Intake.isPending}
                            onClick={() => confirmV2Intake.mutate(item.load_id)}
                          >
                            {confirmV2Intake.isPending ? "Committing…" : "Confirm governed intake"}
                          </button>
                        ) : null}
                      </article>
                    ))}
                  </div>
                ) : (
                  <p className="radar-preview">No v2 intake attachments are awaiting review.</p>
                )}
                {v2Intake.isError || confirmV2Intake.isError || stageV2Intake.isError ? (
                  <p className="radar-error">
                    The controlled intake queue could not be updated. No report was committed.
                  </p>
                ) : null}
              </section>
            ) : null}
            <div className="radar-admin-actions">
              <button type="button" onClick={() => preview.mutate()} disabled={preview.isPending}>
                {preview.isPending ? "Running governed preview…" : "Run governed preview"}
              </button>
              {status.data.admin?.canConfigure ? (
                <button
                  type="button"
                  onClick={() => run.mutate()}
                  disabled={run.isPending || !status.data.enabled}
                >
                  {run.isPending
                    ? "Generating RADAR alerts…"
                    : status.data.testMode
                      ? "Generate staged alerts"
                      : "Generate RADAR alerts"}
                </button>
              ) : null}
              {status.data.admin?.canConfigure ? (
                <button
                  type="button"
                  onClick={() => updateSettings.mutate({ enabled: !status.data.enabled })}
                  disabled={updateSettings.isPending}
                >
                  {status.data.enabled ? "Return to dark launch" : "Enable RADAR workspace"}
                </button>
              ) : null}
              {status.data.admin?.canConfigure ? (
                <button
                  type="button"
                  disabled={updateSettings.isPending}
                  onClick={() => {
                    if (
                      status.data.emailEnabled ||
                      window.confirm(
                        "Enable RADAR email delivery? No message is sent until verified alerts are separately queued and sent."
                      )
                    ) {
                      updateSettings.mutate({ email_enabled: !status.data.emailEnabled });
                    }
                  }}
                >
                  {status.data.emailEnabled ? "Disable email delivery" : "Enable email delivery"}
                </button>
              ) : null}
            </div>
            {preview.data ? (
              <div className="radar-preview">
                <strong>{preview.data.qualifiedCount}</strong> qualified from{" "}
                <strong>{preview.data.candidateCount}</strong> organization/practice candidates. No
                data was written and no email was sent.
              </div>
            ) : null}
            {run.data ? (
              <div className="radar-preview">
                <strong>{run.data.alertCount}</strong> alerts created for{" "}
                <strong>{run.data.recipientCount}</strong> recipients.{" "}
                {status.data.emailEnabled
                  ? "Email delivery is queued separately."
                  : "Email is disabled; nobody was contacted."}
              </div>
            ) : null}
            {run.isError ? (
              <p className="radar-error">
                The RADAR run failed without sending email. Review the run log before trying again.
              </p>
            ) : null}

            <h3>Email release</h3>
            <p className="radar-muted">
              Alert generation never sends email. This separate release gate creates fresh{" "}
              {status.data.magicLinkDays}-day private links, prepares one combined message per
              person, and then requires a final send action.
            </p>
            <div className="radar-preview">
              <strong>{status.data.admin?.readyRecipientCount ?? 0}</strong> recipient links ready
              to prepare · <strong>{status.data.admin?.pendingNotifications ?? 0}</strong> combined
              emails waiting to send.
            </div>
            {status.data.admin?.canConfigure ? (
              <div className="radar-admin-actions">
                <button
                  type="button"
                  disabled={
                    !status.data.enabled ||
                    !status.data.emailEnabled ||
                    !(status.data.admin?.readyRecipientCount ?? 0) ||
                    queueAlerts.isPending
                  }
                  onClick={() => {
                    if (
                      window.confirm(
                        "Prepare one combined RADAR email per recipient? This creates fresh private links but does not send any email."
                      )
                    )
                      queueAlerts.mutate();
                  }}
                >
                  {queueAlerts.isPending ? "Preparing verified emails…" : "Queue verified emails"}
                </button>
                <button
                  type="button"
                  disabled={
                    !status.data.emailEnabled ||
                    !(status.data.admin?.pendingNotifications ?? 0) ||
                    dispatchEmails.isPending
                  }
                  onClick={() => {
                    if (
                      window.confirm(
                        `Send ${status.data.admin?.pendingNotifications ?? 0} queued RADAR emails now? This will contact the listed recipients.`
                      )
                    )
                      dispatchEmails.mutate();
                  }}
                >
                  {dispatchEmails.isPending ? "Sending queued emails…" : "Send queued emails"}
                </button>
              </div>
            ) : null}
            {queueAlerts.data ? (
              <div className="radar-preview">
                <strong>{queueAlerts.data.queuedMessages}</strong> combined emails prepared for{" "}
                <strong>{queueAlerts.data.queuedRecipients}</strong> alert recipients.{" "}
                {queueAlerts.data.skippedMessages
                  ? `${queueAlerts.data.skippedMessages} already-prepared bundles were skipped.`
                  : "No duplicate bundles were created."}
              </div>
            ) : null}
            {queueAlerts.isError ? (
              <p className="radar-error">
                No email was queued. RADAR remains at the prior delivery state.
              </p>
            ) : null}
            {dispatchEmails.data ? (
              <div className="radar-preview">
                <strong>{dispatchEmails.data.sent}</strong> emails sent ·{" "}
                <strong>{dispatchEmails.data.failed}</strong> failed.
              </div>
            ) : null}
            {dispatchEmails.isError ? (
              <p className="radar-error">
                The queued email release did not complete. Review the delivery queue before
                retrying.
              </p>
            ) : null}

            {status.data.admin?.canConfigure ? (
              <>
                <h3>Historical archive access</h3>
                <p className="radar-muted">
                  Every active BDSE user can search shared RADAR history from the last 180 days.
                  Designated researchers can also search older retained records; every archive read
                  is audited.
                </p>
                <div className="radar-admin-actions">
                  <input
                    type="email"
                    value={archiveAccessEmail}
                    onChange={(event) => setArchiveAccessEmail(event.target.value)}
                    placeholder="consultant@ankura.com"
                  />
                  <button
                    type="button"
                    disabled={!archiveAccessEmail.trim() || setArchiveAccess.isPending}
                    onClick={() =>
                      setArchiveAccess.mutate(
                        { email: archiveAccessEmail, enabled: true },
                        { onSuccess: () => setArchiveAccessEmail("") }
                      )
                    }
                  >
                    Grant historical access
                  </button>
                </div>
                {(status.data.admin.archiveGrants ?? []).length ? (
                  <div className="radar-distribution-list">
                    {status.data.admin.archiveGrants.map((grant) => {
                      const relation = Array.isArray(grant.user_profiles)
                        ? grant.user_profiles[0]
                        : grant.user_profiles;
                      const email = relation?.email ?? "Unknown user";
                      return (
                        <article key={grant.archive_access_grant_id}>
                          <div>
                            <strong>{relation?.display_name || email}</strong>
                            <small>{email}</small>
                          </div>
                          <button
                            type="button"
                            disabled={setArchiveAccess.isPending}
                            onClick={() => setArchiveAccess.mutate({ email, enabled: false })}
                          >
                            Remove
                          </button>
                        </article>
                      );
                    })}
                  </div>
                ) : null}
                {setArchiveAccess.isError ? (
                  <p className="radar-error">Historical archive access was not changed.</p>
                ) : null}
              </>
            ) : null}

            <h3>Report intake</h3>
            <p className="radar-muted">
              Preview a recurring report before it becomes RADAR evidence. Previewing writes
              nothing. Committing records one immutable, auditable source load; it does not generate
              alerts or send email.
            </p>
            {status.data.inboundAddress ? (
              <p>
                <strong>Governed intake address:</strong> <code>{status.data.inboundAddress}</code>{" "}
                · inbound processing is {status.data.inboundEmailEnabled ? "enabled" : "disabled"}.
              </p>
            ) : null}
            <div className="radar-intake-controls">
              <label>
                Source
                <select
                  value={intakeSource}
                  onChange={(event) => {
                    setIntakeSource(event.target.value);
                    intakePreview.reset();
                    intakeCommit.reset();
                  }}
                >
                  {intakeSources.map((source) => (
                    <option value={source.source_key} key={source.source_key}>
                      {source.display_name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Report date
                <input
                  type="date"
                  value={intakeDate}
                  onChange={(event) => {
                    setIntakeDate(event.target.value);
                    intakePreview.reset();
                    intakeCommit.reset();
                  }}
                />
              </label>
              <label>
                Report file
                <input
                  type="file"
                  accept=".pdf,.xlsx,.csv,.txt,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain"
                  onChange={(event) => {
                    setIntakeFile(event.target.files?.[0] ?? null);
                    intakePreview.reset();
                    intakeCommit.reset();
                  }}
                />
              </label>
              <div className="radar-admin-actions">
                <button
                  type="button"
                  disabled={!intakePayload || intakePreview.isPending || intakeCommit.isPending}
                  onClick={() => intakePayload && intakePreview.mutate(intakePayload)}
                >
                  {intakePreview.isPending ? "Parsing report…" : "Preview report"}
                </button>
                <button
                  type="button"
                  disabled={!intakePayload || !intakePreview.data?.valid || intakeCommit.isPending}
                  onClick={() => intakePayload && intakeCommit.mutate(intakePayload)}
                >
                  {intakeCommit.isPending ? "Committing report…" : "Commit verified report"}
                </button>
              </div>
            </div>
            {intakePreview.data ? (
              <div className={intakePreview.data.valid ? "radar-preview" : "radar-notice"}>
                <strong>{intakePreview.data.rowCount}</strong> governed records detected ·{" "}
                {intakePreview.data.duplicateExternalKeys} duplicate keys.
                {intakePreview.data.errors.length ? (
                  <ul>
                    {intakePreview.data.errors.map((error) => (
                      <li key={error}>{error}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
            {intakePreview.isError ? (
              <p className="radar-error">
                The report could not be previewed. Confirm that the selected source matches the
                report and that the file is PDF, XLSX, CSV, or TXT under 10 MB.
              </p>
            ) : null}
            {intakeCommit.data ? (
              <div className="radar-preview">
                {intakeCommit.data.idempotent ? (
                  "This exact report was already committed; no duplicate load was created."
                ) : (
                  <>
                    <strong>{intakeCommit.data.committed ?? 0}</strong> records committed ·{" "}
                    <strong>{intakeCommit.data.matched ?? 0}</strong> matched to known organizations
                    · <strong>{intakeCommit.data.excludedUnmatched ?? 0}</strong> retained but
                    excluded from alerting until identity resolves.
                  </>
                )}
              </div>
            ) : null}
            {intakeCommit.isError ? (
              <p className="radar-error">
                The verified report was not committed. No source was enabled and no alert was
                generated.
              </p>
            ) : null}

            <h3>Source health</h3>
            <div className="radar-table-wrap">
              <table className="radar-table">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Authority</th>
                    <th>Intake</th>
                    <th>Freshness</th>
                    <th>Latest load</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {status.data.sources.map((source) => (
                    <tr key={source.source_key}>
                      <td>
                        {source.display_name}
                        {source.outbound_gate ? <small> outbound gate</small> : null}
                      </td>
                      <td>{label(source.authority)}</td>
                      <td>{label(source.intake_mode)}</td>
                      <td>
                        {source.default_freshness_days} days
                        {source.expected_cadence_hours ? (
                          <small>Expected every {source.expected_cadence_hours} hours</small>
                        ) : null}
                      </td>
                      <td>
                        {source.latest_load ? (
                          <>
                            <span>{label(source.latest_load.status)}</span>
                            <small>
                              {source.latest_load.record_count} records ·{" "}
                              {new Date(
                                source.latest_load.committed_at ||
                                  source.latest_load.received_at ||
                                  ""
                              ).toLocaleString()}
                            </small>
                            {source.latest_load.rejection_reason ? (
                              <small>{source.latest_load.rejection_reason}</small>
                            ) : null}
                          </>
                        ) : (
                          <span className="radar-muted">No load recorded</span>
                        )}
                      </td>
                      <td>
                        <span className={source.enabled ? "radar-live" : "radar-off"}>
                          {source.enabled ? "Enabled" : "Off"}
                        </span>
                        {status.data.admin?.canConfigure ? (
                          <button
                            type="button"
                            className="radar-source-toggle"
                            disabled={updateSource.isPending}
                            onClick={() =>
                              updateSource.mutate({
                                sourceKey: source.source_key,
                                enabled: !source.enabled,
                              })
                            }
                          >
                            {source.enabled ? "Disable" : "Enable"}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {updateSource.isError ? (
              <p className="radar-error">
                The source was not changed. Outbound-gate sources require a committed report before
                they can be enabled.
              </p>
            ) : null}

            <h3>RADAR company identity review</h3>
            <p className="radar-muted">
              Exact governed company names and reviewed aliases connect automatically. Uncertain
              matches stay here until a super-admin confirms one; weak or missing matches remain
              queued for company research.
            </p>
            {(status.data.admin?.identityQueue ?? []).length ? (
              <div className="radar-identity-queue">
                {(status.data.admin?.identityQueue ?? []).map((request) => {
                  const selected = identitySelections[request.identity_request_id] ?? "";
                  return (
                    <article key={request.identity_request_id} className="radar-identity-card">
                      <div>
                        <strong>{request.requested_name}</strong>
                        <span
                          className={
                            request.state === "review_required" ? "radar-live" : "radar-off"
                          }
                        >
                          {request.state === "review_required"
                            ? "Review required"
                            : "Research queued"}
                        </span>
                      </div>
                      <p>{request.resolution_note || "Identity resolution is pending."}</p>
                      {request.candidate_snapshot.length ? (
                        <label>
                          Governed candidate
                          <select
                            value={selected}
                            onChange={(event) =>
                              setIdentitySelections((current) => ({
                                ...current,
                                [request.identity_request_id]: event.target.value,
                              }))
                            }
                          >
                            <option value="">Choose a match…</option>
                            {request.candidate_snapshot.map((candidate) => (
                              <option
                                value={candidate.organization_id}
                                key={candidate.organization_id}
                              >
                                {candidate.display_name} ·{" "}
                                {Math.round(candidate.similarity_score * 100)}%
                              </option>
                            ))}
                          </select>
                        </label>
                      ) : (
                        <small>No governed candidate currently clears the review threshold.</small>
                      )}
                      {status.data.admin?.canConfigure ? (
                        <div className="radar-admin-actions">
                          <button
                            type="button"
                            disabled={recheckIdentity.isPending}
                            onClick={() => recheckIdentity.mutate(request.identity_request_id)}
                          >
                            Recheck identity
                          </button>
                          {request.candidate_snapshot.length ? (
                            <button
                              type="button"
                              disabled={!selected || resolveIdentity.isPending}
                              onClick={() =>
                                resolveIdentity.mutate({
                                  identityRequestId: request.identity_request_id,
                                  organizationId: selected,
                                })
                              }
                            >
                              Connect selected match
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            ) : (
              <p className="radar-preview">No RADAR tracker identities need review.</p>
            )}
            {recheckIdentity.isError || resolveIdentity.isError ? (
              <p className="radar-error">
                The identity decision was not changed. Refresh and review the governed candidates
                before trying again.
              </p>
            ) : null}

            <h3>Initial alert distribution</h3>
            <p className="radar-muted">
              Active recipients receive new practice alerts. Removing someone stops future delivery,
              suppresses any unsent bundle, and revokes their active private RADAR links.
            </p>
            {status.data.admin?.canConfigure ? (
              <div className="radar-distribution-editor">
                <label>
                  Name
                  <input
                    value={distributionName}
                    onChange={(event) => setDistributionName(event.target.value)}
                    placeholder="Consultant name"
                  />
                </label>
                <label>
                  Ankura email
                  <input
                    type="email"
                    value={distributionEmail}
                    onChange={(event) => setDistributionEmail(event.target.value)}
                    placeholder="firstname.lastname@ankura.com"
                  />
                </label>
                <label>
                  Practice
                  <select
                    value={distributionPractice}
                    onChange={(event) => setDistributionPractice(event.target.value)}
                  >
                    {status.data.practices.map((key) => (
                      <option value={key} key={key}>
                        {key.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Title
                  <input
                    value={distributionTitle}
                    onChange={(event) => setDistributionTitle(event.target.value)}
                    placeholder="SMD, MD, or practice recipient"
                  />
                </label>
                <button
                  type="button"
                  disabled={
                    upsertDistribution.isPending ||
                    !distributionName.trim() ||
                    !distributionEmail.trim()
                  }
                  onClick={() =>
                    upsertDistribution.mutate(
                      {
                        practiceKey: distributionPractice,
                        email: distributionEmail,
                        displayName: distributionName,
                        title: distributionTitle,
                      },
                      {
                        onSuccess: () => {
                          setDistributionName("");
                          setDistributionEmail("");
                          setDistributionTitle("");
                        },
                      }
                    )
                  }
                >
                  {upsertDistribution.isPending ? "Saving recipient…" : "Add to RADAR distribution"}
                </button>
              </div>
            ) : null}
            <div className="radar-distribution">
              {activeDistribution.map((member) => (
                <div key={member.distribution_member_id}>
                  <strong>{member.display_name}</strong>
                  <span>{member.email}</span>
                  <small>
                    {member.practice_key.toUpperCase()} · {member.title || "Practice recipient"}
                  </small>
                  {status.data.admin?.canConfigure ? (
                    <button
                      type="button"
                      disabled={removeDistribution.isPending}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Remove ${member.display_name} from ${member.practice_key.toUpperCase()} RADAR distribution?`
                          )
                        )
                          removeDistribution.mutate(member.distribution_member_id);
                      }}
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
            {removedDistributionCount ? (
              <small className="radar-muted">
                {removedDistributionCount} removed recipient{" "}
                {removedDistributionCount === 1 ? "is" : "are"} retained in the audit history and
                can be restored by adding the same email and practice again.
              </small>
            ) : null}
            {upsertDistribution.isError || removeDistribution.isError ? (
              <p className="radar-error">
                The RADAR distribution list was not changed. Confirm the Ankura email and practice,
                then try again.
              </p>
            ) : null}

            <h3>Practice scorecard</h3>
            {score.length ? (
              <div className="radar-table-wrap">
                <table className="radar-table">
                  <thead>
                    <tr>
                      <th>Practice</th>
                      <th>Active</th>
                      <th>Meetings</th>
                      <th>Proposals</th>
                      <th>Won</th>
                    </tr>
                  </thead>
                  <tbody>
                    {score.map(([key, metrics]) => (
                      <tr key={key}>
                        <td>{key.toUpperCase()}</td>
                        <td>{metrics.active}</td>
                        <td>{metrics.meetings}</td>
                        <td>{metrics.proposals}</td>
                        <td>{metrics.won}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="radar-muted">No pursuit activity has been recorded yet.</p>
            )}

            <h3>Weekly BD huddle</h3>
            <div className="radar-huddle">
              <label>
                Practice
                <select value={practice} onChange={(event) => setPractice(event.target.value)}>
                  {status.data.practices.map((key) => (
                    <option value={key} key={key}>
                      {key.toUpperCase()}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Facilitator notes
                <textarea
                  value={notes}
                  onChange={(event) =>
                    setDraftNotes((current) => ({ ...current, [practice]: event.target.value }))
                  }
                  placeholder="Decisions, owners, blockers and next moves…"
                />
              </label>
              <button
                type="button"
                disabled={saveHuddle.isPending || !status.data.admin?.weekStart}
                onClick={() =>
                  saveHuddle.mutate({
                    practiceKey: practice,
                    weekStart: status.data?.admin?.weekStart ?? "",
                    notes,
                  })
                }
              >
                {saveHuddle.isPending ? "Saving…" : "Save huddle notes"}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </details>
  );
}
